import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useInvoices } from "../../../lib/invoices/useInvoices";
import {
  Card,
  EmptyState,
  formatCents,
  withinRange,
  computeRange,
  type RangePreset,
} from "./shared";

type Bucket = "overdue" | "thisWeek" | "thisMonth" | "later";

const BUCKET_META: Array<{ key: Bucket; label: string; color: string }> = [
  { key: "overdue", label: "Overdue", color: "#D16B82" },
  { key: "thisWeek", label: "Due this week", color: "#E59F4A" },
  { key: "thisMonth", label: "Due this month", color: "#0070F2" },
  { key: "later", label: "Later", color: "#8691A6" },
];

function daysBetween(aIso: string, bIso: string): number {
  const a = new Date(aIso + "T00:00:00Z").getTime();
  const b = new Date(bIso + "T00:00:00Z").getTime();
  return Math.round((b - a) / 86400000);
}

function bucketFor(dueIso: string | null, todayIso: string): Bucket {
  if (!dueIso) return "later";
  const diff = daysBetween(todayIso, dueIso);
  if (diff < 0) return "overdue";
  if (diff <= 7) return "thisWeek";
  if (diff <= 31) return "thisMonth";
  return "later";
}

export function InvoicesSummary({ preset }: { preset: RangePreset }) {
  const { invoices } = useInvoices();

  const { aging, receivables, payables, totalCount } = useMemo(() => {
    const { startIso, endIso } = computeRange(preset);
    const todayIso = endIso;
    const agingMap: Record<Bucket, number> = {
      overdue: 0,
      thisWeek: 0,
      thisMonth: 0,
      later: 0,
    };
    let recv = 0;
    let pay = 0;
    let count = 0;
    for (const inv of invoices) {
      if (!withinRange(inv.issuedOn, startIso, endIso)) continue;
      count += 1;
      if (inv.direction === "outgoing") {
        recv += inv.amountCents;
      } else {
        pay += inv.amountCents;
        if (inv.status === "paid" || inv.status === "cancelled") continue;
        agingMap[bucketFor(inv.dueOn, todayIso)] += inv.amountCents;
      }
    }
    return {
      aging: [
        {
          bucket: "Invoices",
          overdue: agingMap.overdue,
          thisWeek: agingMap.thisWeek,
          thisMonth: agingMap.thisMonth,
          later: agingMap.later,
        },
      ],
      receivables: recv,
      payables: pay,
      totalCount: count,
    };
  }, [invoices, preset]);

  const agingTotal =
    aging[0].overdue + aging[0].thisWeek + aging[0].thisMonth + aging[0].later;

  return (
    <Card title="Invoices" testId="report-invoices-summary">
      {totalCount === 0 ? (
        <EmptyState message="No invoices in this window yet." />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div
              data-testid="report-receivables"
              className="rounded-md border border-slate-200 bg-slate-50 p-3"
            >
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                Receivables
              </div>
              <div className="text-xl font-semibold text-slate-900 mt-0.5">
                {formatCents(receivables)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Outgoing invoices
              </div>
            </div>
            <div
              data-testid="report-payables"
              className="rounded-md border border-slate-200 bg-slate-50 p-3"
            >
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                Payables
              </div>
              <div className="text-xl font-semibold text-slate-900 mt-0.5">
                {formatCents(payables)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Incoming invoices
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold text-slate-700">
                Aging — incoming
              </div>
              <div className="text-xs text-slate-500">
                {formatCents(agingTotal)}
              </div>
            </div>
            {agingTotal === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">
                No outstanding incoming invoices.
              </div>
            ) : (
              <div className="h-[120px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={aging}
                    layout="vertical"
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                    stackOffset="sign"
                  >
                    <XAxis
                      type="number"
                      tickFormatter={(v) => formatCents(Number(v))}
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="bucket"
                      hide
                    />
                    <Tooltip formatter={(v: number) => formatCents(v)} />
                    <Legend wrapperStyle={{ fontSize: "11px" }} />
                    {BUCKET_META.map((m) => (
                      <Bar
                        key={m.key}
                        dataKey={m.key}
                        name={m.label}
                        stackId="a"
                        fill={m.color}
                        isAnimationActive={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
